import emptyTranscriptionsSrc from 'Assets/images/empy-states/empty-transcriptions.png?inline';
import { ReactComponent as GenerateSummaryIllustration } from 'Assets/images/generate-summary.svg';
import LazyLoader from 'Components/LazyLoader';
import { NetworkEmptyState } from 'Modules/videoHistory/components/NetworkEmptyState';
import {
  useFetchSummary,
  useRegenerateSummary,
} from 'Modules/videoHistory/requests';
import {
  fillEmptySummaryTopicSections,
  isNetworkFetchResult,
  isSummary,
} from 'Modules/videoHistory/utils';
import {
  isBrowserOffline,
  shouldTreatFetchFailureAsNetwork,
} from 'Modules/videoHistory/utils/isNetworkTransportError';
import React from 'react';
import Markdown from 'react-markdown';
import {
  ANALYTICS_EVENT_SUMMARY_COPIED_TEXT,
  ANALYTICS_EVENT_SUMMARY_REGENERATION_CLICKED,
  ANALYTICS_EVENT_SUMMARY_SCROLLED,
  ANALYTICS_EVENT_SUMMARY_VIEWED,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { Button } from '~/lib/ui/button';
import { EmptyState } from '~/lib/ui/emptyState';
import {
  SUMMARY_EMPTY_STATE_DELETED,
  SUMMARY_EMPTY_STATE_FAILED,
  SUMMARY_EMPTY_STATE_PENDING,
  SUMMARY_EMPTY_STATE_UNAVAILABLE,
  SUMMARY_TOPIC_EMPTY_PLACEHOLDER,
} from './index.constants';
import {
  SummaryContentProps,
  SummaryMarkdownContentProps,
} from './index.types';

const SummaryMarkdownContent = ({
  text,
  testId,
  conferenceId,
}: SummaryMarkdownContentProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const hasTrackedScrollRef = React.useRef(false);

  // Track summary viewed once on mount
  React.useEffect(() => {
    if (!conferenceId) {
      return;
    }

    pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_VIEWED, { conferenceId });
  }, [conferenceId]);

  // Track first scroll, fires once per mount, only when content overflows
  React.useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element || !conferenceId) {
      return;
    }

    const handleScroll = () => {
      if (
        hasTrackedScrollRef.current ||
        element.scrollHeight <= element.clientHeight
      ) {
        return;
      }

      hasTrackedScrollRef.current = true;
      pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_SCROLLED, { conferenceId });
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [conferenceId]);

  // Track text copy, only when there is an actual selection
  const handleCopy = () => {
    if (!conferenceId || !window.getSelection()?.toString().trim()) {
      return;
    }

    pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_COPIED_TEXT, { conferenceId });
  };

  const summaryMarkdown = React.useMemo(
    () => fillEmptySummaryTopicSections(text, SUMMARY_TOPIC_EMPTY_PLACEHOLDER),
    [text]
  );

  return (
    <div
      ref={scrollContainerRef}
      className="overflow-y-auto h-full pl-4.5 pr-6"
      data-testid={`${testId}-scroll-container`}
    >
      <div
        className="flex flex-col pt-0 pb-6 text-subtitles"
        data-testid={testId}
        onCopy={handleCopy}
      >
        <Markdown
          components={{
            h1: ({ children }) => (
              <h1 className="text-body font-bold mt-4 mb-2 first:mt-0">
                {children}
              </h1>
            ),
            p: ({ children }) => <p className="text-body">{children}</p>,
            ul: ({ children }) => (
              <ul className="list-disc pl-6 flex flex-col gap-1">{children}</ul>
            ),
            li: ({ children }) => <li className="text-body">{children}</li>,
          }}
        >
          {summaryMarkdown}
        </Markdown>
      </div>
    </div>
  );
};

export const SummaryContent = ({
  testId,
  conferenceId,
  fetchEnabled = true,
  isDeleting = false,
}: SummaryContentProps) => {
  const componentTestId = testId ?? 'summary-content';

  const { data: fetchResult, isPending } = useFetchSummary(conferenceId, {
    enabled: fetchEnabled,
  });
  const {
    mutate: regenerateSummary,
    error: regenerateError,
    isPending: isRegenerating,
  } = useRegenerateSummary(conferenceId);

  const summary = isSummary(fetchResult) ? fetchResult : null;
  const showNetworkEmpty =
    isNetworkFetchResult(fetchResult) ||
    shouldTreatFetchFailureAsNetwork(regenerateError) ||
    (isBrowserOffline() && fetchResult === undefined);
  const isInitialLoad =
    isPending && fetchResult === undefined && !showNetworkEmpty;

  const handleRegenerateClick = () => {
    if (conferenceId) {
      pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_REGENERATION_CLICKED, {
        conferenceId,
      });
    }
    regenerateSummary();
  };

  if (showNetworkEmpty) {
    return <NetworkEmptyState testID={`${componentTestId}-network-error`} />;
  }

  if (isInitialLoad) {
    return <LazyLoader size="small" />;
  }

  if (isDeleting || summary?.status === 'DELETION_PENDING') {
    return (
      <EmptyState
        illustration={
          <img
            src={emptyTranscriptionsSrc}
            alt=""
            aria-hidden
            className="block max-w-full object-contain"
          />
        }
        description={SUMMARY_EMPTY_STATE_DELETED}
        className="h-[65%] min-h-fit"
        descriptionClassName="whitespace-pre-line"
        testID={`${componentTestId}-deleted`}
      />
    );
  }

  if (!summary || summary.status === 'NOT_REQUESTED') {
    return (
      <EmptyState
        illustration={
          <img
            src={emptyTranscriptionsSrc}
            alt=""
            aria-hidden
            className="block max-w-full object-contain"
          />
        }
        description={SUMMARY_EMPTY_STATE_UNAVAILABLE}
        className="h-[65%] min-h-fit"
        descriptionClassName="whitespace-pre-line"
        testID={`${componentTestId}-unavailable`}
      />
    );
  }

  if (summary.status === 'PENDING' || summary.status === 'PROCESSING') {
    return (
      <EmptyState
        illustration={
          <div className="relative h-16 w-16">
            <LazyLoader size="massive" />
          </div>
        }
        description={SUMMARY_EMPTY_STATE_PENDING}
        className="h-[65%] min-h-fit"
        descriptionClassName="whitespace-pre-line"
        testID={`${componentTestId}-pending`}
      />
    );
  }

  if (summary.status === 'FAILED') {
    return (
      <EmptyState
        illustration={
          <GenerateSummaryIllustration
            aria-hidden
            className="block h-[116px] w-[195px] max-w-full object-contain"
          />
        }
        description={SUMMARY_EMPTY_STATE_FAILED}
        className="h-[65%] min-h-fit"
        descriptionClassName="whitespace-pre-line"
        content={
          <Button
            size="lg"
            className="mt-8 px-3"
            disabled={isRegenerating}
            onClick={handleRegenerateClick}
            aria-label="Generate Summary"
            data-testid={`${componentTestId}-regenerate-button`}
          >
            Generate Summary
          </Button>
        }
        testID={`${componentTestId}-failed`}
      />
    );
  }

  if (summary.status === 'AVAILABLE' && summary.text) {
    return (
      <SummaryMarkdownContent
        text={summary.text}
        testId={componentTestId}
        conferenceId={conferenceId}
      />
    );
  }

  return null;
};
